import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cemeteryAPI, importAPI } from '../services/api';
import Toast from '../components/Toast';
import './VerifyBackupPage.css';

function VerifyBackupPage() {
  const [searchParams] = useSearchParams();
  const presetCemetery = searchParams.get('cemetery') || '';
  const presetBackupId = searchParams.get('backupId');

  const [cemeteries, setCemeteries] = useState([]);
  const [selectedCemetery, setSelectedCemetery] = useState(presetCemetery);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    cemeteryAPI
      .list()
      .then((res) => setCemeteries(res.data.cemeteries || res.data || []))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load cemeteries' }));
  }, []);

  const runCompare = useCallback(
    async (opts) => {
      if (!selectedCemetery) {
        setMessage({ type: 'error', text: 'Please select a cemetery' });
        return;
      }
      setLoading(true);
      setMessage(null);
      setResult(null);
      try {
        const res = await importAPI.compareBackup(selectedCemetery, opts);
        setResult(res.data);
        if (!res.data.has_discrepancy) {
          setMessage({ type: 'success', text: 'No discrepancy — backup matches the live DB.' });
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 409 && detail?.code === 'cemetery_mismatch') {
          if (window.confirm(`${detail.message}\n\nCompare anyway?`)) {
            await runCompare({ ...opts, force: true });
            return;
          }
        } else {
          const text =
            typeof detail === 'string' ? detail : detail?.message || 'Comparison failed';
          setMessage({ type: 'error', text });
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedCemetery]
  );

  // Auto-run when arriving from the history "Compare to Live" button.
  useEffect(() => {
    if (presetBackupId && presetCemetery) {
      runCompare({ backupId: presetBackupId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetBackupId, presetCemetery]);

  const handleUpload = () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please choose a backup ZIP file' });
      return;
    }
    runCompare({ file });
  };

  const toggle = (entity) => setExpanded((e) => ({ ...e, [entity]: !e[entity] }));

  const downloadCSV = () => {
    if (!result) return;
    const headers = ['entity', 'direction', 'unique_id', 'label'];
    const rows = [];
    result.entities.forEach((e) => {
      e.missing.forEach((r) =>
        rows.push([e.entity, 'missing', r.unique_id, `"${(r.label || '').replace(/"/g, '""')}"`])
      );
      e.extra.forEach((r) =>
        rows.push([e.entity, 'extra', r.unique_id, `"${(r.label || '').replace(/"/g, '""')}"`])
      );
    });
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `backup-compare-${selectedCemetery}-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-container">
      <h1>Verify Backup</h1>
      <p className="text-muted">
        Compare a backup against the live database to confirm no records are missing or extra.
      </p>

      <div className="card">
        <div className="input-group">
          <label htmlFor="cemetery-select">Cemetery</label>
          <select
            id="cemetery-select"
            value={selectedCemetery}
            onChange={(e) => setSelectedCemetery(e.target.value)}
            disabled={loading}
          >
            <option value="">Select a cemetery…</option>
            {cemeteries.map((c) => (
              <option key={c.unique_name} value={c.unique_name}>
                {c.name || c.unique_name}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="backup-file">Backup ZIP</label>
          <input
            id="backup-file"
            type="file"
            accept=".zip"
            onChange={(e) => setFile(e.target.files[0] || null)}
            disabled={loading}
          />
        </div>

        <button className="btn btn-primary" onClick={handleUpload} disabled={loading}>
          {loading ? 'Comparing…' : 'Compare to Live'}
        </button>
      </div>

      {result && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>
              Result:{' '}
              <span className={result.has_discrepancy ? 'verify-flag-bad' : 'verify-flag-ok'}>
                {result.has_discrepancy ? 'Discrepancy found' : 'Match'}
              </span>
            </h2>
            <button className="btn btn-sm btn-secondary" onClick={downloadCSV}>
              Download CSV
            </button>
          </div>
          <table className="verify-results-table">
            <thead>
              <tr>
                <th>Entity</th>
                <th>Backup</th>
                <th>Live DB</th>
                <th>Missing</th>
                <th>Extra</th>
              </tr>
            </thead>
            <tbody>
              {result.entities.map((e) => {
                const hasDetail = e.missing.length > 0 || e.extra.length > 0;
                return (
                  <React.Fragment key={e.entity}>
                    <tr
                      className={hasDetail ? 'verify-row-clickable' : ''}
                      onClick={() => hasDetail && toggle(e.entity)}
                    >
                      <td>
                        {e.entity}
                        {e.mode === 'count' && (
                          <span className="verify-mode-count"> (count only)</span>
                        )}
                      </td>
                      <td>{e.backup_count}</td>
                      <td>{e.db_count}</td>
                      <td className={e.missing.length ? 'verify-flag-bad' : ''}>
                        {e.missing.length}
                      </td>
                      <td className={e.extra.length ? 'verify-flag-bad' : ''}>
                        {e.extra.length}
                      </td>
                    </tr>
                    {expanded[e.entity] && hasDetail && (
                      <tr className="verify-detail">
                        <td colSpan={5}>
                          {e.missing.length > 0 && (
                            <>
                              <strong>Missing (in backup, not in DB):</strong>
                              <ul>
                                {e.missing.map((r) => (
                                  <li key={`m-${r.unique_id}`}>
                                    {r.label || r.unique_id}{' '}
                                    <span className="text-muted">({r.unique_id})</span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          {e.extra.length > 0 && (
                            <>
                              <strong>Extra (in DB, not in backup):</strong>
                              <ul>
                                {e.extra.map((r) => (
                                  <li key={`x-${r.unique_id}`}>
                                    {r.label || r.unique_id}{' '}
                                    <span className="text-muted">({r.unique_id})</span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {message && <Toast message={message} onDismiss={() => setMessage(null)} />}
    </div>
  );
}

export default VerifyBackupPage;
